<template>
  <v-form ref="form" v-model:model-value="valid" lazy-validation>
    <sf-text-field
      ref="mail-field"
      v-model="mail"
      :label="$t('emailAddress')"
      :rules="[rules.mailRule, rules.requiredRule]"
      density="default"
      required
      autocomplete="username"
      @keydown.enter="login"
    />
    <sf-password-field
      ref="password-field"
      v-model="password"
      class="mt-2"
      :label="$t('password')"
      :rules="[rules.requiredRule]"
      density="default"
      required
      autocomplete="current-password"
      hide-details
      @keydown.enter="login"
    />
    <div v-if="loginError === LoginError.WRONG_CREDENTIALS" class="text-error mt-1">
      {{ $t('error.wrongCredentials') }}
    </div>
    <div v-if="loginError === LoginError.USER_DEACTIVATED" class="text-error mt-1">
      {{ $t('error.userDeactivated') }}
    </div>
    <div v-if="loginError === LoginError.UNKNOWN_ERROR" class="text-error mt-1">
      {{ $t('error.unknownLoginError') }}
    </div>
    <div :class="showRegistration ? 'justify-space-between' : 'justify-center'" class="d-flex mt-2">
      <password-reset-dialog :email="mail" @reset="$emit('reset', $event)" />
      <sf-text-button
        v-if="showRegistration"
        ref="register-button"
        size="small"
        @click="navigateToSelfRegistration"
      >
        {{ $t('register') }}
      </sf-text-button>
    </div>
    <sf-large-button ref="login-button" block class="mt-2" @click="login">
      {{ $t('signIn') }}
    </sf-large-button>
  </v-form>
</template>

<script setup lang="ts">
import SfLargeButton from '@/components/button/SfLargeButton.vue'
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfPasswordField from '@/components/input/SfPasswordField.vue'
import SfTextField from '@/components/input/SfTextField.vue'
import PasswordResetDialog from '@/modules/authentication/components/PasswordResetDialog.vue'
import { LoginCredentials, LoginError, Step } from '@/modules/authentication/types'
import { Branding } from '@/modules/portalSettings/types'
import router from '@/router'
import rules from '@/rules'
import { computed, PropType, Ref, ref } from 'vue'

const props = defineProps({
  branding: {
    type: Object as PropType<Branding>,
    required: true
  },
  loginError: {
    type: String as PropType<LoginError>,
    required: true
  }
})

const emit = defineEmits<{
  (e: 'login', credentials: LoginCredentials): void
  (e: 'reset', mail: string): void
}>()

const form = ref<HTMLFormElement>()
const mail = ref('')
const password = ref('')
const valid = ref(false)
const showRegistration = computed(
  () => props.branding.isUserRegistration && !props.branding.isQuotesPortal
)

const isFormValid = async (checkForm: Ref<HTMLFormElement | undefined>): Promise<boolean> => {
  if (!checkForm.value) {
    return false
  }
  const result = (await checkForm.value.validate()) as unknown as { valid: boolean }
  return result.valid
}

const login = async () => {
  const loginCredentials: LoginCredentials = {
    mail: mail.value,
    password: password.value
  }

  if (await isFormValid(form)) {
    emit('login', loginCredentials)
  }
}

const navigateToSelfRegistration = () => {
  router.push({
    name: 'SelfRegistration',
    params: { step: Step.EMAIL }
  })
}
</script>
